import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { MapMarker } from '../../shared/interfaces';

// Webpack/CRA breaks Leaflet's default icon URL resolution — point the default
// icon at the bundled assets explicitly (classic fix, done once at module load).
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

interface Props {
  titulo?: string;
  markers: MapMarker[];
}

/**
 * Interactive inline map (mostrar_mapa tool): Leaflet + OpenStreetMap tiles with
 * one pin per marker. Popup shows name/address and a Google Maps deep-link.
 * Renders both live (SSE `map` event) and from persisted history content items.
 */
const MapBlock: React.FC<Props> = ({ titulo, markers }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    const valid = (markers || []).filter(
      (m) => typeof m?.lat === 'number' && typeof m?.lng === 'number'
        && m.lat >= -90 && m.lat <= 90 && m.lng >= -180 && m.lng <= 180,
    );
    if (!containerRef.current || valid.length === 0) return;

    const map = L.map(containerRef.current, { scrollWheelZoom: false });
    mapRef.current = map;
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    valid.forEach((m) => {
      const gmaps = `https://www.google.com/maps/search/?api=1&query=${m.lat},${m.lng}`;
      const parts = [
        m.titulo ? `<strong>${escapeHtml(m.titulo)}</strong>` : '',
        m.endereco ? `<div style="margin-top:2px">${escapeHtml(m.endereco)}</div>` : '',
        `<div style="margin-top:4px"><a href="${gmaps}" target="_blank" rel="noopener noreferrer">Abrir no Google Maps</a></div>`,
      ].filter(Boolean);
      L.marker([m.lat, m.lng]).addTo(map).bindPopup(parts.join(''));
    });

    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 15);
    } else {
      map.fitBounds(L.latLngBounds(valid.map((m) => [m.lat, m.lng] as [number, number])), {
        padding: [30, 30],
        maxZoom: 16,
      });
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // markers is data from a persisted content item / SSE payload — key on its
    // JSON so an identical re-render doesn't tear the map down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(markers)]);

  if (!markers || markers.length === 0) return null;

  return (
    <div data-download-exclude style={{ margin: '10px 0' }}>
      {titulo && (
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 6, opacity: 0.85 }}>{titulo}</div>
      )}
      <div
        ref={containerRef}
        style={{
          height: 320,
          width: '100%',
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid rgba(127,127,127,0.25)',
          // Leaflet panes default to a high z-index that floats above app modals.
          zIndex: 0,
          position: 'relative',
        }}
      />
    </div>
  );
};

export default MapBlock;
